import { useEffect, useCallback } from "react"
import type { Puzzle } from "@shared/types.ts"
import type { PuzzleAction } from "./usePuzzleState.ts"
import { getWordCells, type CellClueInfo } from "../lib/grid-utils.ts"
import { playPlaceSound, playDeleteSound } from "../lib/sounds.ts"

interface UseKeyboardNavProps {
  puzzle: Puzzle | null
  currentCell: [number, number] | null
  direction: "across" | "down"
  cells: Record<string, string>
  cellClueMap: Map<string, CellClueInfo>
  dispatch: React.Dispatch<PuzzleAction>
}

export function useKeyboardNav({
  puzzle,
  currentCell,
  direction,
  cells,
  cellClueMap,
  dispatch,
}: UseKeyboardNavProps) {
  const findNextWhiteCell = useCallback(
    (
      row: number,
      col: number,
      dr: number,
      dc: number,
    ): [number, number] | null => {
      if (!puzzle) return null
      const size = puzzle.gridSize
      let r = row + dr
      let c = col + dc
      while (r >= 0 && r < size && c >= 0 && c < size) {
        if (!puzzle.grid[r]![c]!.black) return [r, c]
        r += dr
        c += dc
      }
      return null
    },
    [puzzle],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!puzzle || !currentCell) return
      const [row, col] = currentCell

      // Arrow keys
      if (e.key === "ArrowUp") {
        e.preventDefault()
        const next = findNextWhiteCell(row, col, -1, 0)
        if (next) dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
        return
      }
      if (e.key === "ArrowDown") {
        e.preventDefault()
        const next = findNextWhiteCell(row, col, 1, 0)
        if (next) dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
        return
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        const next = findNextWhiteCell(row, col, 0, -1)
        if (next) dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
        return
      }
      if (e.key === "ArrowRight") {
        e.preventDefault()
        const next = findNextWhiteCell(row, col, 0, 1)
        if (next) dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
        return
      }

      // Space: toggle direction
      if (e.key === " ") {
        e.preventDefault()
        dispatch({ type: "TOGGLE_DIRECTION" })
        return
      }

      // Tab / Shift+Tab: next/previous clue
      if (e.key === "Tab") {
        e.preventDefault()
        // Move to next clue start
        const allClues = [
          ...puzzle.clues.across.map((c) => ({ ...c, dir: "across" as const })),
          ...puzzle.clues.down.map((c) => ({ ...c, dir: "down" as const })),
        ]
        const currentInfo = cellClueMap.get(`${row},${col}`)
        if (!currentInfo) return

        const currentNum = direction === "across" ? currentInfo.across?.number : currentInfo.down?.number
        const currentIdx = allClues.findIndex(
          (c) => c.number === currentNum && c.dir === direction,
        )

        const nextIdx = e.shiftKey
          ? (currentIdx - 1 + allClues.length) % allClues.length
          : (currentIdx + 1) % allClues.length

        const nextClue = allClues[nextIdx]
        if (nextClue) {
          // Find the start cell for this clue
          const grid = puzzle.grid
          for (let r = 0; r < puzzle.gridSize; r++) {
            for (let c = 0; c < puzzle.gridSize; c++) {
              if (grid[r]![c]!.number === nextClue.number) {
                const info = cellClueMap.get(`${r},${c}`)
                if (
                  (nextClue.dir === "across" && info?.across?.number === nextClue.number) ||
                  (nextClue.dir === "down" && info?.down?.number === nextClue.number)
                ) {
                  dispatch({ type: "SELECT_CELL", row: r, col: c })
                  dispatch({ type: "SET_DIRECTION", direction: nextClue.dir })
                  return
                }
              }
            }
          }
        }
        return
      }

      // Letter keys
      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault()
        dispatch({ type: "SET_CELL", row, col, letter: e.key.toUpperCase() })
        playPlaceSound()

        // Auto-advance to next cell in current word direction
        const dr = direction === "down" ? 1 : 0
        const dc = direction === "across" ? 1 : 0
        const next = findNextWhiteCell(row, col, dr, dc)
        if (next) {
          // Only advance if the next cell is in the same word
          const wordCells = getWordCells(puzzle, row, col, direction, cellClueMap)
          const isInWord = wordCells.some(([r, c]) => r === next[0] && c === next[1])
          if (isInWord) {
            dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
          }
        }
        return
      }

      // Backspace: clear current cell and move to previous in word
      if (e.key === "Backspace") {
        e.preventDefault()
        const key = `${row},${col}`

        if (cells[key]) {
          // If cell has content, clear it
          dispatch({ type: "CLEAR_CELL", row, col })
          playDeleteSound()
        }

        // Move to previous cell in current word
        const dr = direction === "down" ? -1 : 0
        const dc = direction === "across" ? -1 : 0
        const prev = findNextWhiteCell(row, col, dr, dc)
        if (prev) {
          const wordCells = getWordCells(puzzle, row, col, direction, cellClueMap)
          const isInWord = wordCells.some(([r, c]) => r === prev[0] && c === prev[1])
          if (isInWord) {
            dispatch({ type: "SELECT_CELL", row: prev[0], col: prev[1] })
          }
        }
        return
      }

      // Delete: clear current cell, don't move
      if (e.key === "Delete") {
        e.preventDefault()
        dispatch({ type: "CLEAR_CELL", row, col })
        playDeleteSound()
        return
      }
    },
    [puzzle, currentCell, direction, cells, cellClueMap, dispatch, findNextWhiteCell],
  )

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])
}
